/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { OemPlanStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OemPlanProps {
  id: string;
  name: string;
  companyId: string;
  oemCompanyId: string;
  outOfServiceReasonId: string;
  defaultBaseId: string;
  planStatus: OemPlanStatus;
  planYear: number;
  description: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OemPlanRecord extends Record<OemPlanProps>({
  id: null as any,
  name: null as any,
  companyId: null as any,
  oemCompanyId: null as any,
  outOfServiceReasonId: null as any,
  defaultBaseId: null as any,
  planStatus: null as any,
  planYear: null as any,
  description: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    companyId: string;
    oemCompanyId: string;
    outOfServiceReasonId: string;
    defaultBaseId: string;
    planStatus: OemPlanStatus;
    planYear: number;
    description: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OemPlanRecord {
    if (json == null) {
      throw new Error('OemPlanRecord.fromJS: parameter cannot be null');
    }
    return new OemPlanRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OemPlanJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'OemPlanJson.name'),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'OemPlanJson.companyId',
      ),
      oemCompanyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'oemCompanyId', '00000000-0000-0000-0000-000000000000'),
        'OemPlanJson.oemCompanyId',
      ),
      outOfServiceReasonId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(j, 'outOfServiceReasonId', '00000000-0000-0000-0000-000000000000'),
        'OemPlanJson.outOfServiceReasonId',
      ),
      defaultBaseId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'defaultBaseId', '00000000-0000-0000-0000-000000000000'),
        'OemPlanJson.defaultBaseId',
      ),
      planStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'planStatus', OemPlanStatus.Draft),
        'OemPlanJson.planStatus',
      ),
      planYear: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'planYear', 0),
        'OemPlanJson.planYear',
      ),
      description: json.description,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    companyId: string;
    oemCompanyId: string;
    outOfServiceReasonId: string;
    defaultBaseId: string;
    planStatus: OemPlanStatus;
    planYear: number;
    description: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OemPlanRecord, Error[]] {
    if (json == null) {
      throw new Error('OemPlanRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OemPlanRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OemPlanJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'OemPlanJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'OemPlanJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oemCompanyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'oemCompanyId', '00000000-0000-0000-0000-000000000000'),
            'OemPlanJson.oemCompanyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfServiceReasonId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'outOfServiceReasonId', '00000000-0000-0000-0000-000000000000'),
            'OemPlanJson.outOfServiceReasonId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      defaultBaseId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'defaultBaseId', '00000000-0000-0000-0000-000000000000'),
            'OemPlanJson.defaultBaseId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      planStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'planStatus', OemPlanStatus.Draft),
            'OemPlanJson.planStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      planYear: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => propValueOrDefault(j, 'planYear', 0), 'OemPlanJson.planYear'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      description: tryOrDefault(
        () => json.description,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OemPlanProps) {
    super(props);
    if (props == null) {
      throw new Error('OemPlanRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OemPlanViewModelBase implements OemPlanProps {
  protected readonly _props: OemPlanProps;
  private _json?: OemPlanProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get companyId(): string {
    return this._props.companyId;
  }
  get oemCompanyId(): string {
    return this._props.oemCompanyId;
  }
  get outOfServiceReasonId(): string {
    return this._props.outOfServiceReasonId;
  }
  get defaultBaseId(): string {
    return this._props.defaultBaseId;
  }
  get planStatus(): OemPlanStatus {
    return this._props.planStatus;
  }
  get planYear(): number {
    return this._props.planYear;
  }
  get description(): string | null {
    return this._props.description;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCancelled() {
    return this._props.planStatus === OemPlanStatus.Cancelled;
  }
  public get isDemobilized() {
    return this._props.planStatus === OemPlanStatus.Demobilized;
  }
  public get isDemobilizing() {
    return this._props.planStatus === OemPlanStatus.Demobilizing;
  }
  public get isDeployed() {
    return this._props.planStatus === OemPlanStatus.Deployed;
  }
  public get isDraft() {
    return this._props.planStatus === OemPlanStatus.Draft;
  }

  public isAnyPlanStatus(...oemPlanStatus: OemPlanStatus[]) {
    return oemPlanStatus.includes(this._props.planStatus);
  }

  constructor(props: OemPlanProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        companyId: this._props.companyId,
        oemCompanyId: this._props.oemCompanyId,
        outOfServiceReasonId: this._props.outOfServiceReasonId,
        defaultBaseId: this._props.defaultBaseId,
        planStatus: this._props.planStatus,
        planYear: this._props.planYear,
        description: this._props.description,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOemPlanEntities = (state: AppState) => state.entityStores.oemPlan.entities;

export const selectOemPlanById = (id: string) => (state: AppState) =>
  state.entityStores.oemPlan.entities.get(id);

export const selectOemPlanMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.oemPlan.meta.get(id);

export const selectOemPlanAndMetadataById = (id: string) =>
  createSelector(selectOemPlanById(id), selectOemPlanMetadataById(id), (oemPlan, meta) => ({
    oemPlan,
    meta,
  }));

export function selectOemPlanEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOemPlanEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OemPlanRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOemPlanEntitiesByName(name: string) {
  return createSelector(selectAllOemPlanEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectOemPlanEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllOemPlanEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectOemPlanEntitiesByOemCompanyId(oemCompanyId: string) {
  return createSelector(selectAllOemPlanEntities, (entities) =>
    entities.filter((e) => e.oemCompanyId === oemCompanyId).valueSeq(),
  );
}

export function selectOemPlanEntitiesByOutOfServiceReasonId(outOfServiceReasonId: string) {
  return createSelector(selectAllOemPlanEntities, (entities) =>
    entities.filter((e) => e.outOfServiceReasonId === outOfServiceReasonId).valueSeq(),
  );
}

export function selectOemPlanEntitiesByDefaultBaseId(defaultBaseId: string) {
  return createSelector(selectAllOemPlanEntities, (entities) =>
    entities.filter((e) => e.defaultBaseId === defaultBaseId).valueSeq(),
  );
}

export function selectOemPlanEntitiesByPlanStatus(planStatus: OemPlanStatus) {
  return createSelector(selectAllOemPlanEntities, (entities) =>
    entities.filter((e) => e.planStatus === planStatus).valueSeq(),
  );
}

export function selectOemPlanEntitiesByPlanYear(planYear: number) {
  return createSelector(selectAllOemPlanEntities, (entities) =>
    entities.filter((e) => e.planYear === planYear).valueSeq(),
  );
}

export function selectOemPlanEntitiesByDescription(description: string) {
  return createSelector(selectAllOemPlanEntities, (entities) =>
    entities.filter((e) => e.description === description).valueSeq(),
  );
}
//#endregion Selectors
